import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import { PlanCard, PlanChip } from './components';
import { COLORS } from './tokens';
import type { AccommodationInput, AccommodationList, PlanAccommodation, PlanCatalog, PlanIep } from './planRecordApi';
import { displayYear, formatPlanDate, previousYear } from './planRecordView';

type Group = { key: AccommodationList; label: string; note: string };

const GROUPS: Group[] = [
  { key: 'classroom', label: 'Classroom', note: '' },
  { key: 'contentTests', label: 'Content tests', note: 'your test coordinator enters the final list in the state test platform' },
  { key: 'access', label: 'ACCESS', note: '' },
];

const SMALL = { fontSize: 12, fontWeight: 600, lineHeight: '16px', color: COLORS.muted } as const;
const BODY = { fontSize: 16, fontWeight: 500, lineHeight: '24px' } as const;

function sourceLabel(a: PlanAccommodation, prevYear: string): string {
  if (a.source === 'suggested') return 'Suggested';
  if (a.source === 'carried') return `From ${displayYear(a.fromYear || prevYear)}`;
  return `Added by ${a.by?.name ?? 'staff'}`;
}

function Footnote({ children }: { children: string }) {
  return <Typography sx={SMALL}>{children}</Typography>;
}

function Muted({ children }: { children: string }) {
  return <Typography sx={{ ...BODY, color: COLORS.muted }}>{children}</Typography>;
}

function Row({ item, prevYear }: { item: PlanAccommodation; prevYear: string }) {
  const dropped = item.status === 'removed';
  return (
    <Box data-testid={`acc-row-${item.itemId}`} data-dropped={dropped ? 'true' : undefined} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 28 }}>
      {dropped ? <RemoveRoundedIcon sx={{ fontSize: 20, color: COLORS.faint }} /> : <CheckRoundedIcon sx={{ fontSize: 20, color: COLORS.navy }} />}
      <Typography sx={{ ...BODY, flex: 1, minWidth: 0, color: dropped ? COLORS.faint : COLORS.ink, textDecoration: dropped ? 'line-through' : 'none' }}>
        {item.label}
      </Typography>
      {item.noClassroomMatch && !dropped && <PlanChip label="No classroom match" tone="warn" />}
      {dropped ? (
        <PlanChip label={`Dropped this year · was in ${displayYear(item.fromYear || prevYear)}`} />
      ) : (
        <PlanChip label={sourceLabel(item, prevYear)} tone={item.source === 'suggested' ? 'suggested' : 'grey'} />
      )}
    </Box>
  );
}

function GroupHeader({ label, count, action }: { label: string; count: string; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: '24px', color: COLORS.muted }}>{label}</Typography>
        <Typography sx={SMALL}>{count}</Typography>
      </Box>
      {action}
    </Box>
  );
}

function noMatchFoot(items: PlanAccommodation[]): string {
  const names = items.map((a) => a.label.replace(/\s*\(.*?\)/, ''));
  const joined = names.join(', ');
  const plural = names.length > 1;
  return `Policy expects test supports to be ones the student uses routinely in class. ${joined} ${plural ? 'have' : 'has'} no matching classroom accommodation on this plan: add one, or remove ${plural ? 'them' : 'this item'}.`;
}

type EditRow = { list: AccommodationList; itemId: string; on: boolean; source: AccommodationInput['source']; fromYear: string; wasOn: boolean };

export function AccommodationsCard({
  firstName,
  districtName,
  year,
  status,
  finalizedAt,
  items,
  iep,
  alternateAccess,
  canEdit,
  catalog,
  onSave,
  onEditingChange,
  pastPlan = null,
}: {
  firstName: string;
  districtName: string;
  year: string;
  status: 'draft' | 'finalized';
  finalizedAt: string;
  items: PlanAccommodation[];
  iep: PlanIep;
  alternateAccess: boolean;
  canEdit: boolean;
  catalog: PlanCatalog | null;
  onSave: (items: AccommodationInput[]) => Promise<void>;
  onEditingChange?: (editing: boolean) => void;
  pastPlan?: { currentYear: string; saved?: boolean } | null;
}) {
  const [editing, setEditing] = useState<EditRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const prevYear = previousYear(year);
  const shownYear = displayYear(year);
  const sisKnows = iep.sis !== null || iep.section504 !== null;
  const accessOpen = iep.sis === true || iep.section504 === true;
  const editable = canEdit && status === 'draft' && catalog !== null;

  const seen = useRef(new Set(items.map((a) => `${a.list}:${a.itemId}`)));
  const rowsRef = useRef<EditRow[] | null>(null);
  const foldedWhileSaving = useRef(false);
  useEffect(() => {
    const arrived = items.filter((a) => a.status !== 'removed' && !seen.current.has(`${a.list}:${a.itemId}`));
    seen.current = new Set(items.map((a) => `${a.list}:${a.itemId}`));
    if (arrived.length === 0) return;
    const folded = rowsRef.current?.map((r) => {
      const late = arrived.find((a) => a.list === r.list && a.itemId === r.itemId);
      return late && !r.on ? { ...r, on: true, source: late.source, fromYear: late.fromYear } : r;
    }) ?? null;
    if (folded) {
      rowsRef.current = folded;
      foldedWhileSaving.current = true;
      setEditing(folded);
    }
  }, [items]);
  const setRows = (rows: EditRow[] | null) => {
    rowsRef.current = rows;
    setEditing(rows);
  };

  const startEditing = () => {
    if (!catalog) return;
    const rows: EditRow[] = [];
    for (const group of GROUPS) {
      for (const it of catalog[group.key]) {
        const current = items.find((a) => a.list === group.key && a.itemId === it.id);
        rows.push({
          list: group.key,
          itemId: it.id,
          on: current != null && current.status !== 'removed',
          source: current?.source ?? 'added',
          fromYear: current?.fromYear ?? '',
          wasOn: current != null && current.status !== 'removed',
        });
      }
    }
    setRows(rows);
    setFailed(false);
    onEditingChange?.(true);
  };
  const stopEditing = () => {
    setRows(null);
    onEditingChange?.(false);
  };
  const toggle = (list: AccommodationList, itemId: string) => {
    if (saving) return;
    setRows(rowsRef.current?.map((r) => (r.list === list && r.itemId === itemId ? { ...r, on: !r.on } : r)) ?? null);
  };
  const save = async () => {
    if (!editing || saving) return;
    setSaving(true);
    setFailed(false);
    try {
      do {
        foldedWhileSaving.current = false;
        const rows = rowsRef.current;
        if (!rows) break;
        await onSave(
          rows
            .filter((r) => r.on || r.wasOn || r.source === 'carried')
            .map((r) => ({ list: r.list, itemId: r.itemId, on: r.on, source: r.source })),
        );
      } while (foldedWhileSaving.current && rowsRef.current);
      stopEditing();
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (editing && catalog) {
    const classroomOn = new Set(editing.filter((r) => r.list === 'classroom' && r.on).map((r) => r.itemId));
    const labelFor = (list: AccommodationList, id: string) => catalog[list].find((it) => it.id === id)?.label ?? id;
    return (
      <PlanCard
        title="Accommodations"
        subtitle={alternateAccess ? `Pick what ${firstName}'s IEP already provides. Nothing is checked for Alternate ACCESS until you choose it.` : `Pick from ${districtName}'s lists. Suggested items and last year's are checked already.`}
        right={
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button onClick={stopEditing} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.ink }}>Cancel</Button>
            <Button variant="contained" disabled={saving} onClick={() => { void save(); }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, bgcolor: COLORS.navy, '&:hover': { bgcolor: '#23236F' } }}>Save</Button>
          </Box>
        }
        sx={{ gap: 3 }}
      >
        {GROUPS.map((group) => {
          const list = catalog[group.key];
          if (group.key === 'access' && !alternateAccess && !accessOpen) {
            return (
              <Box key={group.key} data-testid="acc-edit-access" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <GroupHeader label="ACCESS" count="Not available" />
                <Muted>{`ACCESS accommodations apply only with an IEP or 504 plan. ${firstName}'s SIS record shows neither, so this list is closed.`}</Muted>
              </Box>
            );
          }
          const rows = editing.filter((r) => r.list === group.key);
          const onCount = rows.filter((r) => r.on).length;
          const hint = group.key === 'contentTests' ? ` · should match what ${firstName} uses in class` : group.key === 'access' ? ' · IEP on file; the IEP team decides' : '';
          return (
            <Box key={group.key} data-testid={`acc-edit-${group.key}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <GroupHeader label={group.key === 'access' && alternateAccess ? 'Alternate ACCESS' : group.label} count={`${onCount} of ${list.length} checked${hint}`} />
              {rows.map((r) => {
                const item = catalog[group.key].find((it) => it.id === r.itemId);
                let src = '';
                if (r.on && r.wasOn) src = sourceLabel({ source: r.source, fromYear: r.fromYear, by: null } as PlanAccommodation, prevYear);
                else if (!r.on && r.source === 'carried') src = `Was in ${displayYear(r.fromYear || prevYear)} · unchecking drops it`;
                if (group.key === 'contentTests' && r.on && item?.matchesClassroomItem) {
                  const match = item.matchesClassroomItem;
                  const note = match === 'none' ? 'no classroom counterpart' : classroomOn.has(match) ? `matches ${labelFor('classroom', match).split(' for ')[0]}` : `no classroom match yet: needs ${labelFor('classroom', match).split(' for ')[0]}`;
                  src = src ? `${src} · ${note}` : note;
                }
                return (
                  <Box
                    key={r.itemId}
                    component="button"
                    type="button"
                    role="checkbox"
                    aria-checked={r.on}
                    aria-label={labelFor(group.key, r.itemId)}
                    onClick={() => toggle(group.key, r.itemId)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 32, width: '100%', textAlign: 'left', border: 0, bgcolor: 'transparent', borderRadius: 1.5, px: 0.5, cursor: 'pointer', '&:hover': { bgcolor: COLORS.bg } }}
                  >
                    <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, border: `1px solid ${r.on ? COLORS.navy : COLORS.faint}`, bgcolor: r.on ? COLORS.navy : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.on && <CheckRoundedIcon sx={{ fontSize: 12, color: '#FFFFFF' }} />}
                      </Box>
                    </Box>
                    <Typography sx={{ ...BODY, flex: 1, minWidth: 0, color: r.on ? COLORS.ink : COLORS.muted }}>{labelFor(group.key, r.itemId)}</Typography>
                    {src && <Typography sx={{ ...SMALL, whiteSpace: 'nowrap', color: r.on && r.source === 'suggested' ? COLORS.navy : COLORS.muted }}>{src}</Typography>}
                  </Box>
                );
              })}
            </Box>
          );
        })}
        {failed && <Typography sx={{ fontSize: 13, color: '#B20101' }}>Couldn’t save the accommodations. Try again.</Typography>}
        <Footnote>{`Missing an item? Only items on ${districtName}'s lists can go on a plan. Ask your district administrator to add it in Settings.`}</Footnote>
      </PlanCard>
    );
  }

  if (pastPlan && !pastPlan.saved && items.length === 0) {
    return (
      <PlanCard
        title="Accommodations"
        subtitle={`Not in your file. Accommodations are recorded on plans from ${displayYear(pastPlan.currentYear)}, and ${firstName}'s ${shownYear} plan was generated before then.`}
        sx={{ gap: 0 }}
      >
        {null}
      </PlanCard>
    );
  }

  let subtitle: string;
  if (status === 'finalized') subtitle = `From ${districtName}'s lists. Frozen with the ${shownYear} plan on ${formatPlanDate(finalizedAt)}.`;
  else if (!canEdit) subtitle = `Suggested from ${firstName}'s ACCESS scores and the district lists. This plan can't be changed here.`;
  else if (alternateAccess) subtitle = `Nothing is suggested for students on Alternate ACCESS: those decisions belong to the IEP team. Add what the IEP already provides, from ${districtName}'s lists.`;
  else subtitle = `Suggested from ${firstName}'s ACCESS scores and the district lists. Change anything before finalizing.`;

  return (
    <PlanCard
      title="Accommodations"
      subtitle={subtitle}
      right={
        editable && !alternateAccess ? (
          <Button startIcon={<EditOutlinedIcon />} onClick={startEditing} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy, flexShrink: 0 }}>Edit</Button>
        ) : undefined
      }
      sx={{ gap: 3 }}
    >
      {GROUPS.map((group) => {
        const groupItems = items.filter((a) => a.list === group.key);
        const onItems = groupItems.filter((a) => a.status !== 'removed');
        const dropped = groupItems.length - onItems.length;
        const flagged = group.key === 'contentTests' ? onItems.filter((a) => a.noClassroomMatch) : [];
        const addFromList = editable && alternateAccess ? (
          <Button startIcon={<AddIcon />} onClick={startEditing} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy, p: 0 }}>Add from list</Button>
        ) : undefined;
        let label = group.label;
        let count: string;
        let body: React.ReactNode;
        if (group.key === 'access' && alternateAccess) {
          label = 'Alternate ACCESS';
          count = groupItems.length ? `${onItems.length} in place · allowed with an IEP (on file)` : 'None yet · allowed with an IEP (on file)';
          body = groupItems.length ? (
            <>
              {groupItems.map((a) => <Row key={a.itemId} item={a} prevYear={prevYear} />)}
              <Footnote>Never suggested automatically. The IEP team decides Alternate ACCESS accommodations.</Footnote>
            </>
          ) : <Muted>Never suggested automatically. The IEP team decides Alternate ACCESS accommodations.</Muted>;
        } else if (group.key === 'access' && status === 'draft' && !accessOpen && canEdit) {
          if (!sisKnows) {
            count = 'None';
            body = <Muted>{"IEP and Section 504 aren't in your SIS file, so ACCESS accommodations can't be suggested or added yet. Universal tools and extra time within the school day are open to every student and aren't recorded here."}</Muted>;
          } else {
            count = 'None';
            body = <Muted>{`ACCESS accommodations apply only with an IEP or 504 plan. ${firstName}'s SIS record shows neither. Universal tools and extra time within the school day are open to every student and aren't recorded here.`}</Muted>;
          }
        } else if (group.key === 'access') {
          count = `${onItems.length} in place · allowed with an IEP or 504 plan`;
          const agreement = iep.access === null
            ? ''
            : iep.access && iep.sis === true
              ? ' The SIS and the ACCESS results file both show an IEP.'
              : !iep.access && iep.sis === true
                ? ' The SIS shows an IEP; the ACCESS results file shows none.'
                : iep.access && iep.sis === false
                  ? ' The ACCESS results file shows an IEP; the SIS shows none.'
                  : iep.access
                    ? ' The ACCESS results file shows an IEP; the SIS does not state one.'
                    : '';
          body = (
            <>
              {groupItems.map((a) => <Row key={a.itemId} item={a} prevYear={prevYear} />)}
              <Footnote>{`Never suggested automatically. The IEP team decides ACCESS accommodations; these match the IEP's testing page.${agreement}`}</Footnote>
            </>
          );
        } else if (alternateAccess && groupItems.length === 0) {
          count = 'None yet';
          body = <Muted>{group.key === 'classroom' ? `Not suggested for Alternate ACCESS. Add the classroom accommodations ${firstName}'s IEP provides.` : "Not suggested for Alternate ACCESS. The IEP's testing page decides; add what it lists."}</Muted>;
        } else {
          const bits = [status === 'draft' ? `${onItems.length} suggested` : `${onItems.length} in place`];
          if (dropped) bits.push(`${dropped} dropped`);
          if (flagged.length) bits.push(`${flagged.length} to check`);
          if (group.note) bits.push(group.note);
          count = bits.join(' · ');
          body = (
            <>
              {groupItems.map((a) => <Row key={a.itemId} item={a} prevYear={prevYear} />)}
              {flagged.length > 0 && <Footnote>{noMatchFoot(flagged)}</Footnote>}
            </>
          );
        }
        return (
          <Box key={group.key} data-testid={`acc-group-${group.key}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <GroupHeader label={label} count={count} action={addFromList} />
            {body}
          </Box>
        );
      })}
    </PlanCard>
  );
}
